import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HandCoins, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table'
import ConfirmDialog from '../../schedule/components/ConfirmDialog'
import { formatIsoDate } from '../../schedule/lib/format'
import DetailRow from '../components/DetailRow'
import { compareMoneyStrings } from '../lib/contracts'
import { formatMoney, toEditText } from '../lib/money'
import type { CostRetention } from '../types'
import RetentionReleaseDialog from './RetentionReleaseDialog'

interface Props {
  retention: CostRetention
  currency: string
  locale: string
  canRelease: boolean
  saving: boolean
  onAdd: (input: Record<string, unknown>) => Promise<boolean>
  onDelete: (id: string) => Promise<boolean>
}

/** Retained, released and what remains, with the releases (1b-A spec §5). Retention never moves the budget. */
const ContractRetentionCard: React.FC<Props> = ({ retention, currency, locale, canRelease, saving, onAdd, onDelete }) => {
  const { t } = useTranslation()
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<string | null>(null)
  const money = (v: string) => formatMoney(v, { locale, currency })
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base">{t('CostControl_retention_title')}</CardTitle>
        {canRelease && (
          <Button variant="outline" size="sm" onClick={() => setAdding(true)} disabled={compareMoneyStrings(retention.remaining, '0') <= 0}>
            <HandCoins className="h-4 w-4" />
            {t('CostControl_retention_release')}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <dl className="divide-y">
          <DetailRow label={t('CostControl_contract_field_retentionPercent')}><span className="tabular-nums">{retention.percent ? `${toEditText(retention.percent, locale)} %` : '—'}</span></DetailRow>
          <DetailRow label={t('CostControl_retention_retained')}><span className="tabular-nums">{money(retention.retained)}</span></DetailRow>
          <DetailRow label={t('CostControl_retention_released')}><span className="tabular-nums">{money(retention.released)}</span></DetailRow>
          <DetailRow label={t('CostControl_retention_remaining')}><strong className="tabular-nums">{money(retention.remaining)}</strong></DetailRow>
        </dl>
        {retention.releases.length > 0 && (
          <Table>
            <TableBody>
              {retention.releases.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="whitespace-nowrap text-sm">{formatIsoDate(t, r.date)}</TableCell>
                  <TableCell className="text-sm">{r.note ?? ''}</TableCell>
                  <TableCell className="w-32 text-right tabular-nums">{money(r.amount)}</TableCell>
                  <TableCell className="w-10">
                    {canRelease && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setRemoving(r.id)} aria-label={t('CostControl_retention_release_delete')}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <RetentionReleaseDialog open={adding} remaining={retention.remaining} currency={currency} locale={locale} onOpenChange={setAdding} onSubmit={onAdd} />
      <ConfirmDialog
        open={!!removing}
        title={t('CostControl_retention_release_delete_title')}
        description={t('CostControl_retention_release_delete_text')}
        confirmLabel={t('Common_delete')}
        variant="destructive"
        submitting={saving}
        onCancel={() => setRemoving(null)}
        onConfirm={async () => {
          if (removing) await onDelete(removing)
          setRemoving(null)
        }}
      />
    </Card>
  )
}
export default ContractRetentionCard
